import React from 'react';
import { clsx } from 'clsx';
import { useNavigate, useSearch } from '@tanstack/react-router';

// History SUBTABS (LR-390): Trades | Mark to Market | Cash Pool — a nested second level under
// the top-level `Trades | History` mode tabs (LR-587 retired the third one). LR-498 v3: rendered as folder/
// card tabs (mirroring MtmCounterpartyTabs), NOT the blue underline — that idiom belongs to the
// mode tabs above, and two stacked underline rows collided. The folder idiom yields a clean
// three-level hierarchy: underline (mode) → folder (subtab) → segmented (By date / By
// counterparty in the rail). The selection lives in the `htab` search param (default 'trades')
// so refresh / bookmarks preserve it.
//
// LR-479: the repo cash-pool posting log was lifted out of the Mark to Market subtab (where it
// lived as a segmented toggle) into its own top-level "Cash Pool" subtab here.
const TAB_BUTTON_CLASSES =
  'px-4 py-2.5 text-sm font-medium motion-safe:transition-colors whitespace-nowrap cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export type HistoryTab = 'trades' | 'mtm' | 'cashpool';

const HTAB_LABELS: Record<HistoryTab, string> = {
  trades: 'Trades',
  mtm: 'Mark to Market',
  cashpool: 'Cash Pool',
};

export interface HistorySubTabsProps {
  activeHTab: HistoryTab;
}

export const HistorySubTabs: React.FC<HistorySubTabsProps> = ({ activeHTab }) => {
  const navigate = useNavigate();
  // Read cp from the typed route search (number | undefined) so we can preserve it without
  // spreading prev — prev widens cp to string|number and breaks the navigate search type.
  // LR-672: the counterparty TAB (`counterparty`) and the saved view (`view_id`, LR-671)
  // are the section's scope, orthogonal to the sub-tab — carry them through as well.
  const { cp, counterparty, view_id } = useSearch({ from: '/trades' });

  const go = (htab: HistoryTab): void => {
    if (htab === activeHTab) return;
    // Stay in History; switch only the subtab. Preserve cp (the MTM subtab uses it).
    // LR-498: reset the History master–detail selection (sel) and seed hview to the new
    // tab's natural default — Mark to Market opens "By date", Cash Pool opens "By
    // counterparty". The plain Trades subtab ignores both (left undefined).
    const hview: 'date' | 'cp' | undefined =
      htab === 'mtm' ? 'date' : htab === 'cashpool' ? 'cp' : undefined;
    navigate({
      to: '/trades',
      search: { mode: 'history' as const, cp, htab, hview, sel: undefined, counterparty, view_id },
    });
  };

  return (
    // -mx-6 px-6 cancels the wrapping CardHeader's px-6 so the inset divider runs flush
    // full-width (folder-tab look: the active white card paints over the divider beneath it).
    // Mirrors MtmCounterpartyTabs.
    <div
      className="scroll-x-thin flex items-center gap-1 shadow-[inset_0_-1px_0_0_#cbd5e1] -mx-6 px-6 overflow-x-auto overflow-y-hidden"
      role="tablist"
      aria-label="History view"
    >
      {(['trades', 'mtm', 'cashpool'] as const).map((htab) => {
        const active = htab === activeHTab;
        return (
          <button
            key={htab}
            type="button"
            role="tab"
            aria-selected={active}
            aria-current={active ? 'page' : undefined}
            onClick={() => go(htab)}
            className={clsx(
              TAB_BUTTON_CLASSES,
              active
                ? 'bg-white text-slate-900 font-semibold border-t border-x border-slate-300 rounded-t-lg relative z-10'
                : 'text-slate-500 bg-transparent rounded-md hover:bg-slate-200 hover:text-slate-800',
            )}
          >
            {HTAB_LABELS[htab]}
          </button>
        );
      })}
    </div>
  );
};
